import LegalLayout from '../components/legal/LegalLayout';
import Seo from '../components/Seo';

const H2 = ({ children }) => (
  <h2 className="font-['Josefin_Sans'] text-2xl sm:text-3xl font-bold text-white mt-10 mb-4 tracking-tight">
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="font-mono text-xs text-[#B5FF00] tracking-widest mt-5 mb-2">{children}</h3>
);
const P = ({ children }) => <p className="text-zinc-300">{children}</p>;
const UL = ({ items }) => (
  <ul className="list-none space-y-1.5 ml-1">
    {items.map((item) => (
      <li key={item} className="text-zinc-300 flex gap-3">
        <span className="text-[#B5FF00] flex-shrink-0">·</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const PrivacyPage = () => (
  <LegalLayout title="PRIVACY POLICY" lastUpdated="June 2026">
    <Seo title="Privacy Policy" description="How Unight collects, stores, and protects your personal data across the UK and India." path="/privacy" />
    <section>
      <H2>1. Introduction</H2>
      <P>
        Unight respects your privacy and is committed to protecting your personal information. This Privacy Policy
        explains how we collect, use, store, and protect your information when you use our platform.
      </P>
    </section>

    <section>
      <H2>2. Information We Collect</H2>

      <H3>Account Information</H3>
      <UL items={['Name', 'Email address', 'Phone number', 'Date of birth']} />

      <H3>Profile Information</H3>
      <UL items={['Music preferences', 'Event interests', 'Location preferences']} />

      <H3>Transaction Information</H3>
      <UL items={['Ticket purchases', 'Payment confirmations', 'Booking history']} />

      <H3>Technical Information</H3>
      <UL items={['IP address', 'Device information', 'Browser type', 'Cookies and analytics data']} />
    </section>

    <section>
      <H2>3. How We Use Your Information</H2>
      <P>We use information to:</P>
      <UL items={[
        'Provide ticketing services',
        'Process transactions',
        'Recommend events',
        'Improve platform functionality',
        'Prevent fraud',
        'Send service notifications',
        'Provide customer support',
        'Comply with legal obligations',
      ]} />
    </section>

    <section>
      <H2>4. Event Recommendations</H2>
      <P>
        Unight may use user behaviour, event attendance history, music preferences, and platform activity to provide
        personalized event recommendations.
      </P>
    </section>

    <section>
      <H2>5. Sharing Information</H2>
      <P>We may share information with:</P>
      <UL items={[
        'Event organizers',
        'Payment processors',
        'Analytics providers',
        'Technology partners',
        'Legal authorities when required by law',
      ]} />
      <P className="mt-4">
        <strong className="text-white">We do not sell personal information to third parties.</strong>
      </P>
    </section>

    <section>
      <H2>6. Payment Processing</H2>
      <P>
        Payment information is processed by secure third-party payment providers. Unight does not store full payment
        card information.
      </P>
    </section>

    <section>
      <H2>7. Cookies</H2>
      <P>We use cookies and similar technologies to:</P>
      <UL items={[
        'Maintain login sessions',
        'Improve performance',
        'Measure platform usage',
        'Personalize content',
      ]} />
      <P className="mt-3">Users may control cookies through browser settings.</P>
    </section>

    <section>
      <H2>8. Data Retention</H2>
      <P>
        We retain information only as long as necessary to provide services, comply with legal obligations, and
        resolve disputes.
      </P>
    </section>

    <section>
      <H2>9. Data Security</H2>
      <P>
        We implement reasonable technical and organizational safeguards to protect user data. However, no system can
        guarantee complete security.
      </P>
    </section>

    <section>
      <H2>10. User Rights</H2>
      <P>Depending on applicable law, users may:</P>
      <UL items={[
        'Access their data',
        'Correct inaccurate information',
        'Request deletion',
        'Withdraw consent',
        'Request data portability',
      ]} />
    </section>

    <section>
      <H2>11. International Transfers</H2>
      <P>Data may be processed in multiple countries where Unight or its service providers operate.</P>
    </section>

    <section>
      <H2>12. Children&apos;s Privacy</H2>
      <P>Unight is not intended for users under 18 years of age.</P>
    </section>

    <section>
      <H2>13. Changes to this Policy</H2>
      <P>We may update this Privacy Policy periodically. Updated versions will be published on the platform.</P>
    </section>

    <section>
      <H2>14. Contact</H2>
      <div className="border border-zinc-800 p-5 bg-[#0A0A0A] space-y-2">
        <p className="text-zinc-300">
          Privacy enquiries:{' '}
          <a className="text-[#B5FF00] hover:underline" href="mailto:privacy@unighttickets.com">
            privacy@unighttickets.com
          </a>
        </p>
        <p className="text-zinc-300">
          General support:{' '}
          <a className="text-[#B5FF00] hover:underline" href="mailto:support@unighttickets.com">
            support@unighttickets.com
          </a>
        </p>
      </div>
    </section>
  </LegalLayout>
);

export default PrivacyPage;
